/**
 * 도면 메모(FloorPlanMemoDiagram / FloorPlanMemoText) 렌더링용 fabric 객체 헬퍼.
 *
 * 레거시 mash-renderer의 PolygonObject / TextboxObject를 fabric.Polygon / fabric.Textbox로 포팅.
 * 데이터는 percent 좌표(0~1 또는 0~100) → fabric 픽셀 좌표로 변환해서 사용.
 *
 * 식별 속성: `__memoKind`("diagram" | "text"), `__memoId`(원본 ID).
 * 필요시 `canvas.getObjects().filter(o => (o as any).__memoKind === "diagram")` 등으로 일괄 제거 가능.
 */

import { Rect, Textbox } from "fabric";
import type { FloorPlanMemoDiagram, FloorPlanMemoText } from "~/_legacy/types/analysis";

export interface MemoCoordContext {
  /** percent({x, y}) → pixel({x, y}) 변환 함수 (useFloorPlanCanvas의 percentToPixel 그대로) */
  percentToPixel: (p: { x: number; y: number }) => { x: number; y: number };
  /** pixel({x, y}) → percent({x, y}) 역변환. 편집 시 좌표 저장용. */
  pixelToPercent?: (p: { x: number; y: number }) => { x: number; y: number };
}

type DiagramMeta = { __memoKind: "diagram"; __memoId: number; __floorPlanId: number; __style: FloorPlanMemoDiagram["style"] };
type TextMeta = { __memoKind: "text"; __memoId: number; __floorPlanId: number; __style: FloorPlanMemoText["style"] };

export type TaggedDiagram = Rect & DiagramMeta;
export type TaggedText = Textbox & TextMeta;
export type TaggedMemo = TaggedDiagram | TaggedText;

export function createFloorPlanMemoDiagramObject(
  memo: FloorPlanMemoDiagram,
  ctx: MemoCoordContext,
  options: { editable?: boolean } = {},
): TaggedDiagram {
  const editable = options.editable ?? false;
  const absPoints = memo.coordinates.map((c) => ctx.percentToPixel(c));

  // 4점으로부터 회전 각도, 실제 너비/높이, 중심 좌표를 계산
  let left: number, top: number, width: number, height: number, angleDeg = 0;
  if (absPoints.length >= 4) {
    const dx = absPoints[1].x - absPoints[0].x;
    const dy = absPoints[1].y - absPoints[0].y;
    angleDeg = Math.atan2(dy, dx) * (180 / Math.PI);
    width = Math.sqrt(dx * dx + dy * dy);
    const dx2 = absPoints[3].x - absPoints[0].x;
    const dy2 = absPoints[3].y - absPoints[0].y;
    height = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    // originX="left", originY="top" 기준: left/top = 회전된 top-left 꼭짓점 위치
    left = absPoints[0].x;
    top = absPoints[0].y;
  } else {
    // fallback: 2점 이하 — 기존 bbox 방식
    const minX = Math.min(...absPoints.map((p) => p.x));
    const minY = Math.min(...absPoints.map((p) => p.y));
    const maxX = Math.max(...absPoints.map((p) => p.x));
    const maxY = Math.max(...absPoints.map((p) => p.y));
    left = minX; top = minY; width = maxX - minX; height = maxY - minY;
  }

  const obj = new Rect({
    left,
    top,
    width,
    height,
    angle: angleDeg,
    originX: "left",
    originY: "top",
    fill: memo.style.fill ?? "transparent",
    stroke: memo.style.borderColor,
    strokeWidth: memo.style.borderColor ? 3 : 0,
    strokeUniform: true,
    opacity: memo.style.opacity,
    selectable: editable,
    evented: editable,
    hasControls: editable,
    hasBorders: editable,
    lockScalingX: false,
    lockScalingY: false,
    lockRotation: false,
    transparentCorners: false,
    cornerSize: 8,
    cornerColor: "#484BD2",
    cornerStyle: "circle",
    objectCaching: false,
  }) as TaggedDiagram;
  obj.__memoKind = "diagram";
  obj.__memoId = memo.id;
  obj.__floorPlanId = memo.floorPlanId;
  obj.__style = memo.style;
  return obj;
}

export function createFloorPlanMemoTextObject(
  memo: FloorPlanMemoText,
  ctx: MemoCoordContext,
  options: { editable?: boolean } = {},
): TaggedText {
  const editable = options.editable ?? false;
  const pos = ctx.percentToPixel(memo.position);
  const obj = new Textbox(memo.content, {
    left: pos.x,
    top: pos.y,
    width: memo.style.width,
    angle: memo.style.rotation ?? 0,
    minWidth: 1,
    dynamicMinWidth: 1,
    splitByGrapheme: true, // 1글자 단위로 wrap 가능
    fontSize: memo.style.fontSize,
    fill: memo.style.color,
    opacity: memo.style.opacity,
    fontFamily: "Pretendard Variable, Pretendard, sans-serif",
    originX: "left",
    originY: "top",
    selectable: editable,
    evented: editable,
    editable, // 더블클릭 텍스트 편집
    hasControls: editable,
    hasBorders: editable,
    lockScalingY: true,
    lockRotation: false,
    objectCaching: false,
    // hasStroke 옵션은 fabric의 stroke로 매핑 (white 외곽선 효과)
    stroke: memo.style.hasStroke ? "white" : undefined,
    strokeWidth: memo.style.hasStroke ? 2 : 0,
    paintFirst: "stroke",
  }) as TaggedText;
  if (editable) {
    // 좌우 middle handle만 보여 width만 조절 가능 (글자 폭은 유지, 텍스트는 wrap)
    obj.setControlsVisibility({
      tl: false,
      tr: false,
      bl: false,
      br: false,
      mt: false,
      mb: false,
      mtr: true,
      ml: true,
      mr: true,
    });
  }
  obj.__memoKind = "text";
  obj.__memoId = memo.id;
  obj.__floorPlanId = memo.floorPlanId;
  obj.__style = memo.style;
  return obj;
}

export function isFloorPlanMemoObject(obj: unknown): obj is TaggedMemo {
  return !!obj && typeof obj === "object" && "__memoKind" in obj;
}

/** 신규 텍스트 메모 기본 스타일 (legacy 기본값과 동일) */
export const DEFAULT_TEXT_MEMO_STYLE: FloorPlanMemoText["style"] = {
  color: "#11111F",
  width: 200,
  opacity: 1,
  fontSize: 16,
  hasStroke: true,
};

/** 신규 다이어그램 메모 기본 스타일 */
export const DEFAULT_DIAGRAM_MEMO_STYLE: FloorPlanMemoDiagram["style"] = {
  fill: "rgba(72, 75, 210, 0.12)",
  borderColor: "#484BD2",
  opacity: 1,
};

/** 임시 ID 생성: 절대 서버 ID와 충돌하지 않는 음수 시퀀스 */
let tempIdCounter = -1;
export function nextTempMemoId(): number {
  tempIdCounter -= 1;
  return tempIdCounter;
}

/**
 * 편집 후 fabric Rect로부터 4점을 percent 좌표로 추출.
 * 회전(angle)이 있으면 변환 행렬을 통해 실제 꼭짓점 위치를 계산.
 */
export function extractDiagramCoordinates(
  obj: TaggedDiagram,
  pixelToPercent: (p: { x: number; y: number }) => { x: number; y: number },
): { x: number; y: number }[] {
  const matrix = obj.calcTransformMatrix();
  const w = obj.width ?? 0;
  const h = obj.height ?? 0;
  // 로컬 좌표 4점 (center origin 기준)
  const locals = [
    { x: -w / 2, y: -h / 2 },
    { x:  w / 2, y: -h / 2 },
    { x:  w / 2, y:  h / 2 },
    { x: -w / 2, y:  h / 2 },
  ];
  return locals.map((lp) => {
    const px = lp.x * matrix[0] + lp.y * matrix[2] + matrix[4];
    const py = lp.x * matrix[1] + lp.y * matrix[3] + matrix[5];
    return pixelToPercent({ x: px, y: py });
  });
}

export function extractTextPosition(
  obj: TaggedText,
  pixelToPercent: (p: { x: number; y: number }) => { x: number; y: number },
): { x: number; y: number } {
  return pixelToPercent({ x: obj.left ?? 0, y: obj.top ?? 0 });
}
